import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Users, Trophy, Play } from 'lucide-react';
import { collection, onSnapshot, query, orderBy, addDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { generateFixtures } from '../../utils/sampleData';

const GenerateFixturesPage = () => {
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch leagues
    const leaguesQuery = query(
      collection(db, 'leagues'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeLeagues = onSnapshot(leaguesQuery, (snapshot) => {
      const leaguesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLeagues(leaguesData);
    });

    // Fetch teams
    const teamsQuery = query(
      collection(db, 'teams'),
      orderBy('name', 'asc')
    );

    const unsubscribeTeams = onSnapshot(teamsQuery, (snapshot) => {
      const teamsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTeams(teamsData);
    });

    return () => {
      unsubscribeLeagues();
      unsubscribeTeams();
    };
  }, []);

  const handleGenerateFixtures = async () => {
    if (!selectedLeague || teams.length < 2) {
      setError('Please select a league and ensure you have at least 2 teams');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const fixtures = generateFixtures(teams);
      
      // Create fixtures in Firestore
      for (const fixture of fixtures) {
        const fixtureDate = new Date();
        fixtureDate.setDate(fixtureDate.getDate() + Math.floor(Math.random() * 30));
        
        await addDoc(collection(db, 'fixtures'), {
          ...fixture,
          leagueId: selectedLeague,
          date: fixtureDate.toISOString().split('T')[0],
          time: `${9 + Math.floor(Math.random() * 8)}:00`,
          status: 'upcoming',
          createdAt: new Date().toISOString()
        });
      }

      navigate('/fixtures');
    } catch (error) {
      setError('Failed to generate fixtures. Please try again.');
      console.error('Error generating fixtures:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={() => navigate('/fixtures')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft size={20} />
          Back to Fixtures
        </button>
        <h1 className="text-3xl font-bold mb-2" style={{ color: '#1C563D' }}>
          Generate Fixtures
        </h1>
        <p className="text-gray-600">
          Automatically generate match fixtures for your league
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Generation Form */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="card"
        >
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#1C563D' }}>
            Fixture Generation
          </h2>
          
          <div className="space-y-4">
            <div className="form-group">
              <label htmlFor="league" className="form-label">
                Select League *
              </label>
              <select
                id="league"
                name="league"
                className="form-select"
                value={selectedLeague}
                onChange={(e) => setSelectedLeague(e.target.value)}
              >
                <option value="">Choose a league...</option>
                {leagues.map((league) => (
                  <option key={league.id} value={league.id}>
                    {league.name} - {league.season}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">
                Available Teams ({teams.length})
              </label>
              {teams.length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {teams.map((team) => (
                    <div
                      key={team.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center">
                        <Users size={20} className="mr-2 text-gray-500" />
                        <span className="font-medium">{team.name}</span>
                      </div>
                      <span className="text-sm text-gray-600">
                        {team.players?.length || 0} players
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <Users size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No teams available</p>
                  <p className="text-sm">
                    <a href="/teams/create" style={{ color: '#1C563D' }}>
                      Create teams first
                    </a>
                  </p>
                </div>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <button
              onClick={handleGenerateFixtures}
              disabled={loading || !selectedLeague || teams.length < 2}
              className="btn btn-primary w-full"
              style={{
                backgroundColor: loading || !selectedLeague || teams.length < 2 ? '#999' : '#1C563D',
                cursor: loading || !selectedLeague || teams.length < 2 ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Generating Fixtures...' : 'Generate Fixtures'}
            </button>
          </div>
        </motion.div>

        {/* Preview */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#1C563D' }}>
            Preview
          </h2>
          
          {teams.length >= 2 ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <Calendar size={20} className="mr-2 text-green-600" />
                  <span className="font-semibold text-green-800">
                    Round-Robin Tournament
                  </span>
                </div>
                <p className="text-sm text-green-700">
                  Each team will play every other team once
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Teams:</span>
                  <span className="font-medium">{teams.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Matches:</span>
                  <span className="font-medium">
                    {teams.length * (teams.length - 1) / 2}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Rounds:</span>
                  <span className="font-medium">{teams.length - 1}</span>
                </div>
              </div>

              <div className="mt-4">
                <h3 className="font-semibold mb-2">Sample Fixtures:</h3>
                <div className="space-y-1 text-sm">
                  {teams.slice(0, 3).map((team, index) => (
                    <div key={team.id} className="text-gray-600">
                      {team.name} vs {teams[(index + 1) % teams.length]?.name}
                    </div>
                  ))}
                  {teams.length > 3 && (
                    <div className="text-gray-500">... and more</div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Trophy size={48} className="mx-auto mb-2 opacity-50" />
              <p>Need at least 2 teams to generate fixtures</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default GenerateFixturesPage;
